import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Pencil, Wifi, Clock, Shield } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Card, HStack, VStack, Grid, GridItem } from '@components/base/layout';
import { Tabs, Breadcrumb } from '@components/base/navigation';
import { Alert, Spinner } from '@components/base/feedback';
import { erpRegistriesService } from '../../api/erpRegistriesService';
import type {
  ErpEnvironment,
  TestConnectionResponse,
  RateLimitStatus,
} from '../../api/erpRegistriesService';
import {
  ERP_ENVIRONMENT_LABELS,
  getErpEnvironmentBadgeVariant,
} from '../../configs/erpEnvironment';
import { EndpointsListPanel } from './EndpointsListPanel';
import { ErrorMappingsListPanel } from './ErrorMappingsListPanel';

const AUTH_TYPE_LABELS: Record<string, string> = {
  api_key: 'API Key',
  oauth2_client: 'OAuth2 Client',
  basic: 'Basic',
  bearer: 'Bearer',
  none: 'None',
  login_bearer: 'Login Bearer',
};

/** Environment badge, or a muted dash when the registry has no environment. */
function EnvironmentValue({
  environment,
}: Readonly<{ environment: ErpEnvironment | null }>) {
  if (!environment) {
    return (
      <Text size="sm" color="muted">
        —
      </Text>
    );
  }

  return (
    <Badge variant={getErpEnvironmentBadgeVariant(environment)} size="sm">
      {ERP_ENVIRONMENT_LABELS[environment]}
    </Badge>
  );
}

function DetailField({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <VStack gap={1}>
      <Text size="sm" color="muted" weight="medium">
        {label}
      </Text>
      <div>{children}</div>
    </VStack>
  );
}

function ConnectionTestSection({ registryId }: Readonly<{ registryId: string }>) {
  const [result, setResult] = useState<TestConnectionResponse | null>(null);

  const testMutation = useMutation({
    mutationFn: () => erpRegistriesService.testConnection(registryId),
    onSuccess: (data) => { setResult(data); },
    onError: () => {
      setResult({
        success: false,
        message: 'Failed to test connection. Please try again.',
        response_time_ms: 0,
      });
    },
  });

  return (
    <Card>
      <Card.Header title="Connection Test" />
      <Card.Content>
        <VStack gap={4}>
          <Button
            variant="secondary"
            leftIcon={<Icon icon={Wifi} size="sm" />}
            loading={testMutation.isPending}
            onClick={() => { testMutation.mutate(); }}
          >
            Test Connection
          </Button>

          {result && (
            <VStack gap={2}>
              <HStack gap={2} align="center">
                <Badge variant={result.success ? 'success' : 'error'} size="sm">
                  {result.success ? 'Success' : 'Failed'}
                </Badge>
                <Text size="sm">{result.message}</Text>
              </HStack>
              {result.response_time_ms > 0 && (
                <HStack gap={1} align="center">
                  <Icon icon={Clock} size="xs" color="muted" />
                  <Text size="sm" color="muted">
                    Response time: {result.response_time_ms}ms
                  </Text>
                </HStack>
              )}
            </VStack>
          )}
        </VStack>
      </Card.Content>
    </Card>
  );
}

function RateLimitSection({ registryId }: Readonly<{ registryId: string }>) {
  const {
    data: rateLimitData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin', 'erp', 'registries', registryId, 'rate-limit-status'],
    queryFn: () => erpRegistriesService.getRateLimitStatus(registryId),
  });

  if (isLoading) {
    return (
      <Card>
        <Card.Header title="Rate Limit Status" />
        <Card.Content>
          <HStack gap={2} align="center">
            <Spinner size="sm" />
            <Text size="sm" color="muted">Loading rate limit status...</Text>
          </HStack>
        </Card.Content>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Header title="Rate Limit Status" />
        <Card.Content>
          <Alert variant="error" title="Failed to load rate limit status">
            {error instanceof Error ? error.message : 'An unexpected error occurred.'}
          </Alert>
        </Card.Content>
      </Card>
    );
  }

  const rateLimit = rateLimitData as RateLimitStatus;

  return (
    <Card>
      <Card.Header
        title="Rate Limit Status"
        action={
          <Badge variant={rateLimit.enabled ? 'info' : 'default'} size="sm">
            {rateLimit.enabled ? 'Enabled' : 'Disabled'}
          </Badge>
        }
      />
      <Card.Content>
        {rateLimit.enabled ? (
          <Grid columns={3} gap={4}>
            <GridItem>
              <DetailField label="Usage">
                <Text weight="medium">
                  {rateLimit.remaining} / {rateLimit.limit} remaining
                </Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Window">
                <Text weight="medium">{rateLimit.window_seconds}s</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Resets In">
                <Text weight="medium">{rateLimit.seconds_until_reset}s</Text>
              </DetailField>
            </GridItem>
          </Grid>
        ) : (
          <Text size="sm" color="muted">Rate limiting is not enabled for this registry.</Text>
        )}
      </Card.Content>
    </Card>
  );
}

function OverviewTab({ registryId }: Readonly<{ registryId: string }>) {
  const { data: registryResponse } = useQuery({
    queryKey: ['admin', 'erp', 'registries', registryId],
    queryFn: () => erpRegistriesService.get(registryId),
  });

  const registry = registryResponse?.data;

  /* v8 ignore start */
  if (!registry) return null;
  /* v8 ignore stop */

  return (
    <VStack gap={6}>
      <Card>
        <Card.Header title="Registry Details" />
        <Card.Content>
          <Grid columns={2} gap={4}>
            <GridItem>
              <DetailField label="Slug">
                <Text>{registry.slug}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Name">
                <Text>{registry.name}</Text>
              </DetailField>
            </GridItem>
            <GridItem span={2}>
              <DetailField label="Base URL">
                <Text>{registry.base_url}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Environment">
                <EnvironmentValue environment={registry.environment} />
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Auth Type">
                <Badge variant="info" size="sm">
                  {AUTH_TYPE_LABELS[registry.auth_type] ?? registry.auth_type}
                </Badge>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Timeout">
                <Text>{registry.timeout_ms}ms</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Default Headers">
                <Text size="sm" color="muted">
                  {registry.default_headers
                    ? JSON.stringify(registry.default_headers)
                    : 'None'}
                </Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Retry Policy">
                <Text size="sm" color="muted">
                  {registry.retry_policy
                    ? JSON.stringify(registry.retry_policy)
                    : 'None'}
                </Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Created">
                <Text size="sm">{new Date(registry.created_at).toLocaleString()}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Updated">
                <Text size="sm">{new Date(registry.updated_at).toLocaleString()}</Text>
              </DetailField>
            </GridItem>
          </Grid>
        </Card.Content>
      </Card>

      {/* Connection test */}
      <ConnectionTestSection registryId={registryId} />

      {/* Rate limit status */}
      <RateLimitSection registryId={registryId} />
    </VStack>
  );
}

export function RegistryDetailPage() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  /* v8 ignore start */
  const slugOrEmpty = slug ?? '';
  /* v8 ignore stop */

  // ---- Fetch registry data -------------------------------------------------

  const {
    data: registryResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin', 'erp', 'registries', slug],
    queryFn: () => {
      /* v8 ignore start */
      if (!slug) return Promise.reject(new Error('Registry slug is required'));
      /* v8 ignore stop */
      return erpRegistriesService.get(slug);
    },
    enabled: !!slug,
  });

  const registry = registryResponse?.data;

  // ---- Render: loading state -----------------------------------------------

  if (isLoading) {
    return (
      <VStack gap={6} align="center" className="py-12">
        <Spinner size="lg" />
        <Text color="muted">Loading registry...</Text>
      </VStack>
    );
  }

  // ---- Render: error state -------------------------------------------------

  if (error || !registry) {
    return (
      <VStack gap={6}>
        <Breadcrumb>
          <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
          <Breadcrumb.Item href="/admin/erp/registries">ERP Registries</Breadcrumb.Item>
          <Breadcrumb.Item isCurrent>Details</Breadcrumb.Item>
        </Breadcrumb>
        <Alert variant="error" title="Failed to load registry">
          {error instanceof Error
            ? error.message
            : 'Registry not found or an unexpected error occurred.'}
        </Alert>
      </VStack>
    );
  }

  // ---- Render: main --------------------------------------------------------

  return (
    <VStack gap={6}>
      {/* Breadcrumb navigation */}
      <Breadcrumb>
        <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
        <Breadcrumb.Item href="/admin/erp/registries">ERP Registries</Breadcrumb.Item>
        <Breadcrumb.Item isCurrent>{registry.name}</Breadcrumb.Item>
      </Breadcrumb>

      {/* Page header */}
      <HStack justify="between" align="start">
        <VStack gap={2}>
          <HStack gap={3} align="center">
            <Text as="h1" size="2xl" weight="bold">
              {registry.name}
            </Text>
            <Badge
              variant={registry.is_active ? 'success' : 'default'}
              size="sm"
            >
              {registry.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </HStack>
          <HStack gap={4} align="center">
            <Text size="sm" color="muted">{registry.base_url}</Text>
            <Badge variant="info" size="sm">
              {AUTH_TYPE_LABELS[registry.auth_type] ?? registry.auth_type}
            </Badge>
            {registry.environment && (
              <EnvironmentValue environment={registry.environment} />
            )}
          </HStack>
        </VStack>
        <Button
          variant="secondary"
          leftIcon={<Icon icon={Pencil} size="sm" />}
          onClick={() => { navigate(`/admin/erp/registries/${slugOrEmpty}/edit`); }}
        >
          Edit
        </Button>
      </HStack>

      {/* Stats cards */}
      <HStack gap={4}>
        <Card padding="sm">
          <Card.Content>
            <HStack gap={2} align="center">
              <Icon icon={Shield} size="sm" color="primary" />
              <VStack gap={0}>
                <Text size="xs" color="muted">Endpoints</Text>
                <Text weight="bold">{registry.endpoints_count}</Text>
              </VStack>
            </HStack>
          </Card.Content>
        </Card>
        <Card padding="sm">
          <Card.Content>
            <HStack gap={2} align="center">
              <Icon icon={Shield} size="sm" color="primary" />
              <VStack gap={0}>
                <Text size="xs" color="muted">Error Mappings</Text>
                <Text weight="bold">{registry.error_mappings_count}</Text>
              </VStack>
            </HStack>
          </Card.Content>
        </Card>
      </HStack>

      {/* Tabbed content */}
      <Tabs defaultValue="overview">
        <Tabs.List>
          <Tabs.Tab value="overview">Overview</Tabs.Tab>
          <Tabs.Tab value="endpoints">Endpoints</Tabs.Tab>
          <Tabs.Tab value="error-mappings">Error Mappings</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview">
          {slug && <OverviewTab registryId={slug} />}
        </Tabs.Panel>

        <Tabs.Panel value="endpoints">
          {slug && <EndpointsListPanel registryId={slug} />}
        </Tabs.Panel>

        <Tabs.Panel value="error-mappings">
          {slug && <ErrorMappingsListPanel registryId={slug} />}
        </Tabs.Panel>
      </Tabs>
    </VStack>
  );
}
